import React, { useState, useRef } from 'react';
import { useAuthStore } from '../../hooks/useAuthStore';
import LegalModal from '../Legal/LegalModal';
import api, { absUrl } from '../../services/api';
import '../../styles/settings.css';

export default function SettingsPanel({ onClose, onThemeChange }) {
  const { user, updateUser, logout } = useAuthStore();
  const [tab, setTab] = useState('profile');
  const [status, setStatus] = useState(user?.status || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [theme, setTheme] = useState(user?.theme || 'light');
  const [bgColor, setBgColor] = useState(user?.bgColor || '#ffffff');
  const [chatBgColor, setChatBgColor] = useState(user?.chatBgColor || '#f0f0f0');
  const [textColor, setTextColor] = useState(user?.textColor || '#000000');
  const [chatTextColor, setChatTextColor] = useState(user?.chatTextColor || '#000000');
  const [msgBgColor, setMsgBgColor] = useState(user?.msgBgColor || '#dcf8c6');
  const [systemTextColor, setSystemTextColor] = useState(user?.systemTextColor || '#666666');
  const [avatarHistory, setAvatarHistory] = useState([]);
  const [showAvatarHistory, setShowAvatarHistory] = useState(false);
  const [legalModal, setLegalModal] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [emailChangeMessage, setEmailChangeMessage] = useState('');
  const fileInputRef = useRef(null);
  const chatBgRef = useRef(null);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarError('');
    setAvatarLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/users/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      updateUser({ avatarUrl: res.data.url });
    } catch (err) {
      setAvatarError(err.response?.data?.message || 'Ошибка загрузки аватарки');
    }
    setAvatarLoading(false);
  };

  const loadAvatarHistory = async () => {
    const res = await api.get('/users/avatars');
    setAvatarHistory(res.data);
    setShowAvatarHistory(true);
  };

  const handleSaveTheme = () => {
    updateUser({ theme, bgColor, chatBgColor, textColor, chatTextColor, msgBgColor, systemTextColor });
    onThemeChange(theme);
    if (theme === 'custom') {
      applyCustomColors();
    } else {
      clearCustomColors();
    }
    api.put('/users/theme', { theme, bgColor, chatBgColor, textColor, chatTextColor, msgBgColor, systemTextColor });
  };

  const applyCustomColors = (overrides = {}) => {
    const root = document.documentElement;
    root.style.setProperty('--bg', overrides.bgColor ?? bgColor);
    root.style.setProperty('--chat-bg', overrides.chatBgColor ?? chatBgColor);
    root.style.setProperty('--text', overrides.textColor ?? textColor);
    root.style.setProperty('--chat-text', overrides.chatTextColor ?? chatTextColor);
    root.style.setProperty('--msg-bg', overrides.msgBgColor ?? msgBgColor);
    root.style.setProperty('--primary', overrides.textColor ?? textColor);
    root.style.setProperty('--system-text', overrides.systemTextColor ?? systemTextColor);
    root.style.setProperty('--border', overrides.textColor ?? textColor + '33');
    root.style.setProperty('--hover-bg', overrides.bgColor ?? bgColor);
  };

  const clearCustomColors = () => {
    const root = document.documentElement;
    root.style.removeProperty('--bg');
    root.style.removeProperty('--chat-bg');
    root.style.removeProperty('--text');
    root.style.removeProperty('--chat-text');
    root.style.removeProperty('--msg-bg');
    root.style.removeProperty('--primary');
    root.style.removeProperty('--system-text');
    root.style.removeProperty('--border');
    root.style.removeProperty('--hover-bg');
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Вы уверены, что хотите удалить аккаунт? Это действие необратимо.')) {
      try {
        await api.delete('/users/account');
      } catch (err) {
        alert(err.response?.data?.message || 'Не удалось удалить аккаунт');
        return;
      }
      logout();
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!newPassword) { setPasswordError('Введите новый пароль'); return; }
    if (newPassword.length < 8) { setPasswordError('Пароль должен содержать минимум 8 символов'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('Пароли не совпадают'); return; }
    try {
      const encoded = btoa(newPassword);
      await api.put('/users/password', { password: encoded });
      alert('Пароль изменён');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPasswordError(err.response?.data?.message || 'Ошибка смены пароля');
    }
  };

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2>Настройки</h2>
          <button className="settings-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="settings-tabs">
          <button className={tab === 'profile' ? 'active' : ''} onClick={() => setTab('profile')}>Профиль</button>
          <button className={tab === 'appearance' ? 'active' : ''} onClick={() => setTab('appearance')}>Оформление</button>
          <button className={tab === 'subscription' ? 'active' : ''} onClick={() => setTab('subscription')}>MashaGPT</button>
          <button className={tab === 'security' ? 'active' : ''} onClick={() => setTab('security')}>Безопасность</button>
        </div>
        <div className="settings-content">
          {tab === 'profile' && (
            <div className="settings-section">
              <div className="avatar-section">
                <img src={absUrl(user?.avatarUrl) || '/cat.svg'} alt="" className="settings-avatar"
                  onClick={loadAvatarHistory} onError={(e) => { e.target.onerror = null; e.target.src = '/cat.svg'; }} />
                <div className="avatar-upload-label" onClick={() => fileInputRef.current?.click()}>
                  {avatarLoading ? 'Загрузка...' : 'Загрузить аватар'}
                </div>
                <input type="file" accept="image/*" ref={fileInputRef} onChange={handleAvatarUpload} style={{ display: 'none' }} />
                {avatarError && <div className="settings-error">{avatarError}</div>}
              </div>
              {showAvatarHistory && (
                <div className="avatar-history">
                  {avatarHistory.map((url, i) => (
                    <img key={i} src={absUrl(url)} alt="" onClick={() => updateUser({ avatarUrl: url })} />
                  ))}
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input value={user?.email || ''} disabled />
                <button className="settings-btn primary" style={{ marginTop: '8px', padding: '8px 16px', fontSize: '13px' }}
                  onClick={() => setShowEmailChange(true)}>Обновить email</button>
              </div>
              {showEmailChange && (
                <div className="settings-section" style={{ padding: '12px', background: 'var(--hover-bg, #f5f5f5)', borderRadius: '8px' }}>
                  <div className="form-group">
                    <label>Новый email</label>
                    <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Введите новый email" />
                  </div>
                  {emailChangeMessage && <div className={emailChangeMessage.includes('Ошибка') ? 'settings-error' : 'settings-success'}>{emailChangeMessage}</div>}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="settings-btn primary" style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                      onClick={async () => {
                        if (!newEmail || !newEmail.includes('@')) { setEmailChangeMessage('Введите корректный email'); return; }
                        if (!window.confirm(`Вы уверены, что хотите изменить email на ${newEmail}? На новый адрес будет отправлено письмо с подтверждением.`)) return;
                        try {
                          const res = await api.post('/auth/change-email', { newEmail });
                          setEmailChangeMessage(res.data.message || 'Письмо отправлено');
                          setNewEmail('');
                        } catch (err) {
                          setEmailChangeMessage('Ошибка: ' + (err.response?.data?.message || 'Не удалось отправить'));
                        }
                      }}>Отправить подтверждение</button>
                    <button className="settings-btn" style={{ flex: 1, padding: '8px', fontSize: '13px', background: '#f0f0f0', color: '#333' }}
                      onClick={() => { setShowEmailChange(false); setNewEmail(''); setEmailChangeMessage(''); }}>Отмена</button>
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Статус</label>
                <input value={status} onChange={(e) => setStatus(e.target.value)}
                  placeholder="Ваш статус..." />
              </div>
              <button className="settings-btn primary" onClick={async () => {
                await api.put('/users/status', { status });
                updateUser({ status });
              }}>Сохранить статус</button>
            </div>
          )}
          {tab === 'appearance' && (
            <div className="settings-section">
              <div className="form-group">
                <label>Тема</label>
                <select value={theme} onChange={(e) => {
                  const val = e.target.value;
                  setTheme(val);
                }}>
                  <option value="light">Светлая</option>
                  <option value="dark">Тёмная</option>
                  <option value="blue">Синяя</option>
                  <option value="purple">Фиолетовая</option>
                  <option value="green">Зелёная</option>
                  <option value="custom">Пользовательская</option>
                </select>
              </div>
              {theme === 'custom' && (
                <>
                  <div className="form-group">
                    <label>Цвет фона приложения</label>
                    <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Цвет фона чата</label>
                    <input type="color" value={chatBgColor} onChange={(e) => setChatBgColor(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Цвет текста приложения</label>
                    <input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Цвет текста чата</label>
                    <input type="color" value={chatTextColor} onChange={(e) => setChatTextColor(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Цвет фона сообщения</label>
                    <input type="color" value={msgBgColor} onChange={(e) => setMsgBgColor(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Цвет системного текста (подсказки, заголовки)</label>
                    <input type="color" value={systemTextColor} onChange={(e) => setSystemTextColor(e.target.value)} />
                  </div>
                </>
              )}
              <button className="settings-btn primary" onClick={handleSaveTheme}>Применить тему</button>
              <hr className="settings-divider" />
              <div className="form-group">
                <label>Фоновое изображение чата</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <input type="file" accept="image/*" style={{ display: 'none' }} ref={chatBgRef}
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await api.post('/users/chat-bg', formData, {
                            headers: { 'Content-Type': 'multipart/form-data' },
                          });
                          updateUser({ chatBgImage: res.data.url });
                        } catch (err) {
                          console.error('Failed to upload chat background:', err);
                        }
                      }} />
                    <button className="settings-btn" style={{ background: 'var(--hover-bg, #f0f0f0)', color: 'var(--text, #333)', width: '100%' }}
                      onClick={() => chatBgRef.current?.click()}>
                      {user?.chatBgImage ? 'Заменить фон' : 'Загрузить фон'}
                    </button>
                  </div>
                  {user?.chatBgImage && (
                    <button className="settings-btn danger" style={{ padding: '8px 12px', fontSize: '12px' }}
                      onClick={async () => {
                        try {
                          await api.delete('/users/chat-bg');
                          updateUser({ chatBgImage: null });
                        } catch (err) {
                          console.error('Failed to remove chat background:', err);
                        }
                      }}>Убрать</button>
                  )}
                </div>
                {user?.chatBgImage && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={absUrl(user.chatBgImage)} alt="" style={{ width: '100%', height: '80px', objectFit: 'cover', borderRadius: '8px' }} />
                  </div>
                )}
              </div>
            </div>
          )}
          {tab === 'security' && (
            <div className="settings-section">
              {passwordError && <div className="settings-error">{passwordError}</div>}
              <div className="form-group">
                <label>Новый пароль</label>
                <div className="password-wrapper">
                  <input type={showPassword ? 'text' : 'password'} value={newPassword}
                    onChange={(e) => { setNewPassword(e.target.value); setPasswordError(''); }}
                    placeholder="Введите новый пароль" />
                  <span className="password-toggle" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? '🙈' : '👁️'}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label>Повторите пароль</label>
                <div className="password-wrapper">
                  <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                    placeholder="Повторите новый пароль" />
                  <span className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </span>
                </div>
              </div>
              <button className="settings-btn primary" onClick={handleChangePassword}>Сменить пароль</button>
              <hr className="settings-divider" />
              <div className="form-group">
                <label>Документы</label>
                <div className="settings-hint" style={{ lineHeight: '1.8' }}>
                  <a href="#!" onClick={(e) => { e.preventDefault(); setLegalModal('privacy'); }} style={{ color: '#1e88e5' }}>Политика конфиденциальности</a>
                  <br />
                  <a href="#!" onClick={(e) => { e.preventDefault(); setLegalModal('terms'); }} style={{ color: '#1e88e5' }}>Правила пользования</a>
                </div>
              </div>
              <hr className="settings-divider" />
              <button className="settings-btn danger" onClick={handleDeleteAccount}>Удалить аккаунт</button>
            </div>
          )}
          {tab === 'subscription' && <SubscriptionSection />}
        </div>
      </div>
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />}
    </div>
  );
}

function SubscriptionSection() {
  const mashagptUrl = 'https://mashagpt.link/FW6jPV7Bf';

  return (
    <div className="settings-section">
      <h3 style={{ margin: '0 0 8px' }}>MashaGPT</h3>
      <div className="settings-hint">
        Для большего количества генерации картинок и для работы с другими нейросетями рекомендуем зарегистрироваться у нашего партнёра MashaGPT. Это набор нейронок под разные задачи: генерация картинок, видео, смена образов и многое другое. Демократичные цены на покупку токенов. Переходите по{' '}
        <a href={mashagptUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1e88e5' }}>ссылке</a>{' '}
        и регистрируйтесь!
      </div>
      <button className="settings-btn primary" onClick={() => window.open(mashagptUrl, '_blank', 'noopener,noreferrer')} style={{ marginTop: '12px' }}>
        Начать работу с MashaGPT
      </button>
    </div>
  );
}
